import {createSlice} from '@reduxjs/toolkit'
import type {PayloadAction} from '@reduxjs/toolkit'
import type {ModalState} from '../../types/modal'

interface ConfirmDialogState {
    isOpen: boolean
    title: string
    text: string
    confirmButtonTitle: string
    confirmButtonAction: (() => void) | null
}

interface UIState {
    confirmDialog: ConfirmDialogState
    openDropdownId: string | null
    openModal: ModalState
    globalLoading: boolean
    focusedEditorId: string | null
    signInError: string | null
    resetPasswordError: string | null
    isMobileSidebarOpen: boolean
    loading: string[]
    teamColumns: 1 | 2 | 3
    isGuestModified: boolean
}

const initialState: UIState = {
    confirmDialog: {
        isOpen: false,
        title: '',
        text: '',
        confirmButtonTitle: '',
        confirmButtonAction: null
    },
    openDropdownId: null,
    openModal: null,
    globalLoading: true,
    focusedEditorId: null,
    signInError: null,
    resetPasswordError: null,
    isMobileSidebarOpen: false,
    loading: [],
    teamColumns: (() => {
        const saved = localStorage.getItem('teamColumns')
        return saved ? (Number(saved) as 1 | 2 | 3) : 1
    })(),
    isGuestModified: false,
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        openConfirmDialog: (state, action: PayloadAction<{
            title: string
            text: string
            confirmButtonTitle: string
            confirmButtonAction: () => void
        }>) => {
            state.confirmDialog = {
                isOpen: true,
                title: action.payload.title,
                text: action.payload.text,
                confirmButtonTitle: action.payload.confirmButtonTitle,
                confirmButtonAction: action.payload.confirmButtonAction
            }
        },
        closeConfirmDialog: (state) => {
            state.confirmDialog = {
                isOpen: false,
                title: '',
                text: '',
                confirmButtonTitle: '',
                confirmButtonAction: null
            }
        },
        setOpenDropdown: (state, action: PayloadAction<string | null>) => {
            state.openDropdownId = action.payload
        },
        setOpenModal: (state, action: PayloadAction<ModalState>) => {
            state.openModal = action.payload
        },
        setGlobalLoading: (state, action: PayloadAction<boolean>) => {
            state.globalLoading = action.payload
        },
        setFocusedEditor: (state, action: PayloadAction<string | null>) => {
            state.focusedEditorId = action.payload
        },
        setSignInError: (state, action: PayloadAction<string | null>) => {
            state.signInError = action.payload
        },
        setResetPasswordError: (state, action: PayloadAction<string | null>) => {
            state.resetPasswordError = action.payload
        },
        setMobileSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.isMobileSidebarOpen = action.payload
        },
        addLoadingKey: (state, action: PayloadAction<string>) => {
            if (!state.loading.includes(action.payload)) {
                state.loading.push(action.payload)
            }
        },
        removeLoadingKey: (state, action: PayloadAction<string>) => {
            state.loading = state.loading.filter(k => k !== action.payload)
        },
        setTeamColumns: (state, action: PayloadAction<1 | 2 | 3>) => {
            state.teamColumns = action.payload
            localStorage.setItem('teamColumns', String(action.payload))
        },
        setGuestModified: (state, action: PayloadAction<boolean>) => {
            state.isGuestModified = action.payload
        },
    }
})

export const {openConfirmDialog, closeConfirmDialog, setOpenDropdown, setOpenModal, setGlobalLoading, setFocusedEditor, setSignInError, setResetPasswordError, setMobileSidebarOpen, addLoadingKey, removeLoadingKey, setTeamColumns, setGuestModified} = uiSlice.actions
export default uiSlice.reducer
